import { DatePicker } from '@mui/x-date-pickers'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import { Stack, TextField } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import { useEffect, useRef, useState } from 'react'
import createDialogTitle from '../generic/utilities/dialogUtil'
import DoneIcon from '@mui/icons-material/Done'
import { apiPut } from '../generic/Api_Functions'
import moment from 'moment'
import AlertUI from '../generic/AlertUI'
import OtherDevicesConfirmDialog from '../Mos/Widgets/FabricationOrderJobReference/PlannedJobs/components/JobStatusChangeDialog/OtherDevicesConfirmDialog'

const CONFIRMATION_REQUIRED = 'CONFIRMATION_REQUIRED'
const OTHER_DEVICES_REASON = 'JOB_ACTIVE_IN_OTHER_DEVICES'

/**
 * Finishes a planned job retroactively, on its planned device, between the two dates shown.
 *
 * A job still running on OTHER machines follows the same two steps as the MOS finish: the first call
 * sends `interruptInOtherDevices=false`, and Sun answers HTTP 200 `{ type: 'CONFIRMATION_REQUIRED',
 * reason: 'JOB_ACTIVE_IN_OTHER_DEVICES', activeDevices }` without writing anything. Confirming resends
 * it with `true`: Sun interrupts the job on those machines and finishes it as always. Declining writes
 * nothing at all.
 */
const FinishJobDialog = ({ open, onClose, fabricationOrder, jobReference, duration, onDone, plannerDetailId }) => {
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const [start, setStart] = useState(null)
  const [end, setEnd] = useState(null)
  const [otherDevices, setOtherDevices] = useState(null)
  const [loading, setLoading] = useState(false)
  const inFlightRef = useRef(false)
  const [AlertElement, showAlert, close] = AlertUI()

  useEffect(() => {
    if (open) {
      close()
      setOtherDevices(null)
      const endDate = moment()

      const [h, m, s] = duration.split(':').map(Number)

      const startDate = endDate.clone().subtract({
        hours: h,
        minutes: m,
        seconds: s
      })
      setStart(startDate.toDate())
      setEnd(endDate.toDate())
    }
  }, [open, duration, close])

  const renderDatePicker = (label, value, setValue) => {
    return (
      <DatePicker
        disableMaskedInput
        label={label}
        format={format.dateTime}
        value={value}
        onChange={setValue}
        renderInput={(params) => <TextField {...params} />}
      />
    )
  }

  const finishJob = async (interruptInOtherDevices) => {
    // A second click while the first request is on its way would finish the job twice.
    if (inFlightRef.current) return
    inFlightRef.current = true
    setLoading(true)
    try {
      const response = await apiPut('planner/setJobFinished/' + start + '/' + end + '/' + plannerDetailId +
        '?interruptInOtherDevices=' + interruptInOtherDevices)
      if (response?.type === CONFIRMATION_REQUIRED && response.reason === OTHER_DEVICES_REASON) {
        setOtherDevices(Array.isArray(response.activeDevices) ? response.activeDevices : [])
        return
      }
      setOtherDevices(null)
      onClose()
      onDone()
    } catch (error) {
      setOtherDevices(null)
      showAlert({
        title: error.message,
        severity: 'error'
      })
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }

  return (
    <>
      <SmartDialog
        setOpen={open}
        acceptCallback={() => finishJob(false)}
        cancelCallback={onClose}
        renderComponent={
          <Stack gap={1}>
            {AlertElement}
            <h3>{t_('This will mark the job as done ok')}</h3>
            <Stack direction='row' gap={3} justifyContent='space-between'>
              {renderDatePicker(t_('Job Start'), start, setStart)}
              {renderDatePicker(t_('Job End'), end, setEnd)}
            </Stack>
          </Stack>
        }
        title={createDialogTitle({ color: 'primary', title: t_('Finish') + ' ' + fabricationOrder + ' - ' + jobReference, icon: DoneIcon })}
      />
      {open && otherDevices && (
        <OtherDevicesConfirmDialog
          devices={otherDevices}
          loading={loading}
          onAccept={() => finishJob(true)}
          onCancel={() => setOtherDevices(null)}
        />
      )}
    </>
  )
}

export default FinishJobDialog
